import { useState, useEffect, useCallback } from 'react';
import { getUnreadCount } from '../utils/notificationApi';
import { useRole } from '../contexts/RoleContext';

// How often the badge re-checks the unread count while the tab is visible
const POLL_INTERVAL_MS = 60 * 1000;

/**
 * Custom hook to fetch and manage notification count
 * Automatically fetches unread notification count and provides methods to update it.
 * Keeps the badge fresh in-session: polls while the tab is visible, pauses
 * while it's hidden, and refetches immediately on window focus.
 */
const useNotificationCount = () => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { userRole } = useRole();

  // Fetch unread count from API
  const fetchCount = useCallback(async () => {
    // Only fetch if user is authenticated (has a role)
    if (!userRole) {
      setCount(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const unreadCount = await getUnreadCount();
      setCount(unreadCount);
    } catch (err) {
      console.error('Error fetching notification count:', err);
      setError(err.message);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [userRole]);

  // Initial fetch, then keep the count fresh while the user stays logged in
  useEffect(() => {
    fetchCount();

    if (!userRole) {
      return undefined;
    }

    let intervalId = null;

    const startPolling = () => {
      if (intervalId === null) {
        intervalId = setInterval(fetchCount, POLL_INTERVAL_MS);
      }
    };

    const stopPolling = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Don't poll a hidden tab; catch up immediately when it becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stopPolling();
      } else {
        fetchCount();
        startPolling();
      }
    };

    const handleFocus = () => {
      fetchCount();
    };

    if (document.visibilityState !== 'hidden') {
      startPolling();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [userRole, fetchCount]);

  // Method to update count (useful when marking notifications as read)
  const updateCount = (newCount) => {
    setCount(Math.max(0, newCount));
  };

  // Method to decrement count by 1 (when marking single notification as read)
  const decrementCount = () => {
    setCount(prev => Math.max(0, prev - 1));
  };

  // Method to reset count to 0 (when marking all as read)
  const resetCount = () => {
    setCount(0);
  };

  // Method to increment count (when new notification arrives)
  const incrementCount = () => {
    setCount(prev => prev + 1);
  };

  // Method to refresh count from server
  const refreshCount = () => {
    fetchCount();
  };

  return {
    count,
    loading,
    error,
    updateCount,
    decrementCount,
    resetCount,
    incrementCount,
    refreshCount,
  };
};

export default useNotificationCount;
